import React, {useCallback, useState, useEffect} from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  FlatList,
  TextInput
} from 'react-native';
import {styles, getZoneSettingsStyles} from './styles';
import I18n from 'i18n-js';
import {useDispatch, useSelector} from 'react-redux';
import {
  addZone,
  deleteZone,
  fetchZones,
  updateZone,
  updateZoneDataLocal
} from '../../actions/zoneActions';
import {getNextZoneName, ensureZonePrefix} from '../../utils/zoneUtils';
import {ActivityIndicator} from 'react-native-web';
import Toast, {useToast} from '../../commons/Toast';
import ZoneCard from './zoneCard';
import useWidth from '../scanToCartScanning/useWidth';
import {useNavigation} from '@react-navigation/native';
import UnsavedChangesBar from '../../commons/unsavedChanges/UnsavedChangesBar';
import UnsavedChangesPrompt from '../../commons/unsavedChanges/UnsavedChangesPrompt';
import useUnsavedChanges from '../../commons/unsavedChanges/useUnsavedChanges';

const ZoneSettings = () => {
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const screenWidth = useWidth();
  const responsive = getZoneSettingsStyles(screenWidth);
  const [editingZoneId, setEditingZoneId] = useState(null);
  const {showToast, toastMessage, showToastMessage, hideToast} = useToast();
  const zones = useSelector(state => state.zone.zones);
  const navigation = useNavigation();

  const callBack = () => {
    setIsLoading(false);
  };

  useEffect(() => {
    setIsLoading(true);
    dispatch(fetchZones(callBack));
  }, []);

  const addNewZone = () => {
    const zoneName = getNextZoneName(zones);
    const tempId = `temp_${Date.now()}`;
    const newZone = {
      id: tempId,
      zoneName: zoneName,
      name: zoneName,
      ranges: [
        {
          rangeStart: '',
          rangeEnd: ''
        }
      ]
    };

    dispatch({
      type: 'ADD_ZONE_LOCAL',
      payload: newZone
    });
    setEditingZoneId(tempId);
  };

  const addRange = zoneId => {
    const zone = zones.find(z => z.id === zoneId);
    if (zone) {
      const updatedZone = {
        ...zone,
        ranges: [
          ...zone.ranges,
          {
            rangeStart: '',
            rangeEnd: ''
          }
        ]
      };
      dispatch(updateZoneDataLocal(updatedZone));
      setEditingZoneId(zoneId);
      showToastMessage(
        I18n.t('zoneSettings.rangeAddedToast', {zoneName: zone.zoneName})
      );
    }
  };

  const deleteRange = (zoneId, rangeIndex) => {
    const zone = zones.find(z => z.id === zoneId);
    if (zone) {
      if (zone.ranges.length === 1) {
        dispatch(deleteZone(zone.id,
          () => {
            showToastMessage(
              I18n.t('zoneSettings.zoneDeletedToast', {zoneName: zone.zoneName})
            );
          }
        ))
      } else {
        const updatedZone = {
          ...zone,
          ranges: zone.ranges.map((range, index) => {
            if (index === rangeIndex) {
              if (range.id) {
                return {...range, _destroy: true};
              }
              return null;
            }
            return range;
          }).filter(range => range !== null)
        };
        dispatch(updateZoneDataLocal(updatedZone));
        setEditingZoneId(zoneId);
        showToastMessage(
          I18n.t('zoneSettings.rangeDeletedToast', {zoneName: zone.zoneName})
        );
      }
    }
  };

  const updateZoneName = (zoneId, newName) => {
    const zone = zones.find(z => z.id === zoneId);
    if (zone) {
      const nameWithPrefix = ensureZonePrefix(newName);
      const updatedZone = {
        ...zone,
        zoneName: nameWithPrefix,
        name: nameWithPrefix
      };
      dispatch(updateZoneDataLocal(updatedZone));
      setEditingZoneId(zoneId);
    }
  };

  const updateRangeValue = (zoneId, rangeIndex, field, value) => {
    const zone = zones.find(z => z.id === zoneId);
    if (zone) {
      const updatedZone = {
        ...zone,
        ranges: zone.ranges.map((range, index) =>
          index === rangeIndex ? {...range, [field]: value} : range
        )
      };
      dispatch(updateZoneDataLocal(updatedZone));
      setEditingZoneId(zoneId);
    }
  };

  const handleZoneUpdate = zoneId => {
    const zone = zones.find(z => z.id === zoneId);
    if (zone) {
      const isNewZone = zoneId.toString().startsWith('temp_');
      if (isNewZone) {
        dispatch(addZone(zone,
          (createdZone) => {
            setEditingZoneId(null);
            showToastMessage(I18n.t('zoneSettings.zoneAddedToast', {zoneName: createdZone.zoneName}));
          }
        ))
      } else {
        dispatch(updateZone(zoneId, zone,
          () => {
            setEditingZoneId(null);
            showToastMessage(I18n.t('zoneSettings.saveSuccessToast'));
          }
        ))
      }
    }
  };

  const hasUnsavedChanges = editingZoneId !== null;

  const saveEditingZone = useCallback(async () => {
    if (editingZoneId !== null) {
      handleZoneUpdate(editingZoneId);
    }
  }, [editingZoneId, zones]);

  const discardEditingZone = useCallback(() => {
    setEditingZoneId(null);
  }, []);

  const unsavedChanges = useUnsavedChanges({
    hasUnsavedChanges,
    onSave: saveEditingZone,
    onDiscard: discardEditingZone,
    navigation
  });

  const renderZone = ({item: zone}) => (
    <ZoneCard
      zone={zone}
      editingZoneId={editingZoneId}
      onUpdateZoneName={updateZoneName}
      onAddRange={addRange}
      onDeleteRange={deleteRange}
      onUpdateRangeValue={updateRangeValue}
      onSave={handleZoneUpdate}
    />
  );

  return (
    <View style={[styles.container, responsive.container]}>
      <UnsavedChangesBar
        visible={hasUnsavedChanges}
        saving={unsavedChanges.saving}
        onSave={unsavedChanges.handleSave}
        onDiscard={unsavedChanges.handleDiscard}
      />
      <UnsavedChangesPrompt
        visible={unsavedChanges.promptVisible}
        saving={unsavedChanges.saving}
        onSave={unsavedChanges.handleSave}
        onDiscard={unsavedChanges.handleDiscard}
        onStay={unsavedChanges.handleStay}
      />
      <Text style={[styles.header, responsive.header]}>
        {I18n.t('zoneSettings.zonesLabel')}
      </Text>
      <View style={styles.zonesContainer}>
        {isLoading ? (
          <ActivityIndicator size="large" color="#0000ff" />
        ) : (
          <FlatList
            data={zones}
            renderItem={renderZone}
            keyExtractor={item => item.id}
          />
        )}
        <TouchableOpacity
          testID="addZoneButton"
          style={[styles.addZoneButton, responsive.addZoneButton]}
          onPress={addNewZone}>
          <i className="icon-plus" style={{color: '#ffff', fontSize: responsive.addZoneIconSize}}></i>
          <Text
            style={[
              styles.addZoneText,
              responsive.addZoneText
            ]}>
            {I18n.t('zoneSettings.addZoneLabel')}
          </Text>
        </TouchableOpacity>
      </View>

      <Toast visible={showToast} message={toastMessage} onHide={hideToast} />
    </View>
  );
};

export default ZoneSettings;
